const DATE_OPTIONS: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
const TIME_OPTIONS: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
const DEFAULT_SEPARATOR = ' • ';

export type DateInput = string | Date;

export type IntlDateFormatOptions = {
	locale: string;
	hour12?: boolean;
	separator?: string;
};

const formatters = new Map<string, Intl.DateTimeFormat>();

const getFormatter = (cacheKey: string, locale: string, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat => {
	const cached = formatters.get(cacheKey);

	if (cached) {
		return cached;
	}

	let formatter: Intl.DateTimeFormat;

	try {
		// i18next lowercases and underscores tags (vi_VN -> vi_vn), which Intl rejects outright.
		formatter = new Intl.DateTimeFormat(locale.replace(/_/g, '-'), options);
	} catch {
		formatter = new Intl.DateTimeFormat(undefined, options);
	}

	formatters.set(cacheKey, formatter);

	return formatter;
};

const parse = (value: DateInput): Date | null => {
	const date = new Date(value);

	return Number.isNaN(date.getTime()) ? null : date;
};

const dateFormatter = (locale: string) => getFormatter(`date|${locale}`, locale, DATE_OPTIONS);

const timeFormatter = (locale: string, hour12?: boolean) =>
	getFormatter(`time|${locale}|${hour12}`, locale, { ...TIME_OPTIONS, hour12 });

export const formatDate = (isoDate: DateInput, { locale }: IntlDateFormatOptions): string => {
	const date = parse(isoDate);

	return date ? dateFormatter(locale).format(date) : '';
};

export const formatDateTime = (
	isoDate: DateInput,
	{ locale, hour12, separator = DEFAULT_SEPARATOR }: IntlDateFormatOptions
): string => {
	const date = parse(isoDate);

	if (!date) {
		return '';
	}

	return `${dateFormatter(locale).format(date)}${separator}${timeFormatter(locale, hour12).format(date)}`;
};
